<template>
    <div class="payment-modal">
        <form @submit.prevent="handleSubmit" class="stripe-form">
            <p class="text-zinc-950 font-inter text-lg font-semibold">Payment Method</p>
            <div class="divider"></div>
            <div>
                <div class="name-container">
                    <div class="name-div">
                        <label>First name</label>
                        <prime-input-text placeholder="John" :class="{ 'Input-invalid': submitted && !first_name }" v-model="first_name" />
                        <Message severity="error" v-if="submitted && !first_name" class="-mt-2 text-red-600">
                            First name is required.
                        </Message>
                    </div>
                    <div class="name-div">
                        <label>Last name</label>
                        <prime-input-text placeholder="Doe" :class="{ 'Input-invalid': submitted && !last_name }" v-model="last_name" />
                        <Message severity="error" v-if="submitted && !last_name" class="-mt-2 text-red-600">
                            Last name is required.
                        </Message>
                    </div>
                </div>
                <div id="card-element"></div>
            </div>
            <div class="flex justify-end mt-4 gap-2">
                <PrimeButton label="Cancel" @click="closeDialog" variant="secondary" class="p-btn-root_secondary" />
                <PrimeButton label="Update" type="submit" :loading="paymentLoader" />
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import PrimeInputText from 'primevue/inputtext';
import Message from 'primevue/message';
import { onMounted, ref } from 'vue';
import { ModalNames } from '~/types/modals';

const dialogRef =   getDialogRef(ModalNames.UPDATE_PAYMENT_INFO);
const closeDialog = () => {
    dialogRef.value.close();
};
const { showSuccess, showError, showWarning } = useGlobalToast();
const config = useRuntimeConfig();
const paymentLoader = ref(false);

const stripe = ref();
const elements = ref();
const first_name = ref('');
const last_name = ref('');
const submitted = ref(false);

onMounted(async () => {
    setupStripe()
});

const setupStripe = async () => {
    const stripeModule = await import('@stripe/stripe-js');
        stripe.value = await stripeModule.loadStripe(config.public.pb.stripeKey);
        const options = {
            mode: "setup",
            currency: "usd",
            paymentMethodCreation: "manual",
            paymentMethodTypes: ["card"]

        };
        elements.value = stripe.value.elements(options);
        const card = elements.value.create("payment", {
            fields: {
                billingDetails: "auto",
            },
            wallets: { googlePay: "never", applePay: "never", link: "never" }
        });
        card.mount('#card-element');
};

const handleSubmit = async () => {
    submitted.value = true;
    
    if (!first_name.value || !last_name.value) {
        return;
    }
    
    paymentLoader.value = true;
    await elements.value.submit();

    const { error, paymentMethod } = await stripe.value.createPaymentMethod({
        elements: elements.value,
        params: {
            billing_details: {
                name: first_name.value + ' ' + last_name.value,
            }
        },
    });

    if (error) {
        paymentLoader.value = false;
        showError('Error', `Payment error: ${error.message}`);
        return;
    }

    if (paymentMethod.id) {
        paymentLoader.value = false;
        // Call Nuxt hook with the payment method ID
        const nuxtApp = useNuxtApp();
        await nuxtApp.callHook('payment_method:success', { paymentMethodId: paymentMethod.id });
        closeDialog();
    } else {
        paymentLoader.value = false;
    }
};
</script>
<style lang="scss" scoped>
.card-element {
    @apply w-full;
}

.payment-modal {
    @apply sm:w-full w-11/12 mx-auto lg:max-w-2xl rounded-lg bg-white p-8;
}

.name-div {
    @apply flex flex-col mb-3;

    label {
        @apply text-zinc-800 text-xs mb-1;
    }
}

.main-footer {
    @apply flex flex-col justify-center items-end pt-8;
}

.divider {
    @apply h-px self-stretch bg-zinc-300 my-5;
}

.actions-div {
    @apply flex justify-end items-center gap-3 px-6 pb-6;
}

.name-container {
    @apply flex flex-col lg:flex-row gap-1 lg:gap-4;
}
.stripe-form{
    @apply w-full;
}
</style>
